/* eslint-disable max-len */
/**
 * @file 管理路由
 * @author gaoxunchen
 */

import _ from 'lodash';
import React from 'react';
import { Loading } from '@tencent/ptp-fe-common';
import loadable from '@loadable/component';

import perms from '@/common/permissions/sale/perms';
import { isOa } from '@/utils';
import { ACCOUNT_TYPE } from '@/constants/Constants';
import { EMPLOYEE_TYPE } from '@/pages/organization/employee/constants';

import { ROLES_NAME_TO_ID } from '@/pages/agency/permission-config';
import { oneOf } from './utils';

export default [
  {
    label: '管理',
    key: 'manage',
    desc: '管理服务商帐号、员工信息，客户标签管理，业绩管理',
    path: 'agency/manage',
    disabled: (loginStatus: Record<string, any>) => loginStatus.role_list[ROLES_NAME_TO_ID.AGENCYOPERATORROLE_AGENCY_OBSERVER_OPERATOR],
    breathingLight: (account: any) => {
      return account.navBreathingLight?.contractManage;
    },
    data: [
      {
        label: '人员管理',
        key: 'organization-manage',
        iconClassName: 'icon-nav-orgnization',
        data: [
          {
            label: '服务商员工管理',
            key: 'organization',
            href: '/organization/organization',
            path: '/organization/:type',
            perm: (loginStatus: Record<string, any>) => loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id,
            data: [
              // is_enable_strict_employee_rule 新版
              {
                label: '组织架构',
                href: '/organization/organization',
                key: 'organization',
                perm: (loginStatus: Record<string, any>) => {
                  if (!loginStatus.hasTechAgency && !loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/Layout'), { fallback: <Loading /> }),
                basePage: true,
                isMicroApp: true,
              },
              {
                label: '组织架构',
                href: '/organization/organization',
                key: 'organization',
                perm: (loginStatus: Record<string, any>) => {
                  if (loginStatus.hasTechAgency || !!loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/Layout'), { fallback: <Loading /> }),
                basePage: true,
              },
              // is_enable_strict_employee_rule 新版
              {
                label: '角色管理',
                href: '/organization/role',
                key: 'role',
                perm: (loginStatus: Record<string, any>) => {
                  if (!loginStatus.hasTechAgency && !loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/Layout'), { fallback: <Loading /> }),
                basePage: true,
                isMicroApp: true,
              },
              {
                label: '角色管理',
                href: '/organization/role',
                key: 'role',
                perm: (loginStatus: Record<string, any>) => {
                  if (loginStatus.hasTechAgency || !!loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/Layout'), { fallback: <Loading /> }),
                basePage: true,
              },
              {
                label: '员工管理',
                href: '/organization/employee',
                key: 'employee',
                perm: (loginStatus: Record<string, any>) => {
                  if (loginStatus.hasTechAgency || !!loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/employee'), { fallback: <Loading /> }),
                basePage: true,
                tip: '腾讯营销服务商系统为服务商提供数据查询、管理等中立的技术服务，腾讯在提供服务过程不会实际使用、分析、处理服务商的数据，服务商应自行保证数据的合法性，包括但不限于个人信息等数据的合法授权、加密存储、传输安全等，并由服务商独立承担数据相关的义务和责任。',
              },
              {
                // is_enable_strict_employee_rule 新版
                label: '员工管理',
                href: '/organization/employee',
                key: 'employee_new',
                perm: (loginStatus: Record<string, any>) => {
                  if (!loginStatus.hasTechAgency && !loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/employee/new'), { fallback: <Loading /> }),
                basePage: true,
                isMicroApp: true,
                tip: '腾讯营销服务商系统为服务商提供数据查询、管理等中立的技术服务，腾讯在提供服务过程不会实际使用、分析、处理服务商的数据，服务商应自行保证数据的合法性，包括但不限于个人信息等数据的合法授权、加密存储、传输安全等，并由服务商独立承担数据相关的义务和责任。',
              },
              {
                // is_enable_strict_employee_rule 新版
                label: '员工审核',
                href: '/organization/cert-audit',
                key: 'cert-audit',
                perm: (loginStatus: Record<string, any>) => {
                  // 非灰度用户不可见
                  if (!loginStatus.hasTechAgency && !loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  // 非签框svip用户不可见
                  if ((loginStatus.userVerifyInfo?.isSvip || loginStatus.userVerifyInfo?.isNonFrame) && !loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/employee/new'), { fallback: <Loading /> }),
                basePage: true,
                isMicroApp: true,
                tip: '员工提交的主体资质注册申请，需要管理员审核',
              },
              {
                // is_enable_strict_employee_rule 新版
                label: '新增员工',
                href: '/organization/employee/create',
                key: 'employee_create',
                perm: (loginStatus: Record<string, any>) => {
                  if (!loginStatus.hasTechAgency && !loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                isMicroApp: true,
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/employee/edit/new'), { fallback: <Loading /> }),
                hide: true,
                hideTabNav: true,
                hideSubNav: true,
                showSideNav: false,
              },
              {
                // is_enable_strict_employee_rule 新版
                label: '编辑员工',
                href: '/organization/employee/edit',
                key: 'employee_edit',
                perm: (loginStatus: Record<string, any>) => {
                  if (!loginStatus.hasTechAgency && !loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                isMicroApp: true,
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/employee/edit/new'), { fallback: <Loading /> }),
                hide: true,
                hideTabNav: true,
                hideSubNav: true,
                showSideNav: false,
              },
              {
                label: '新增员工',
                href: '/organization/employee/edit',
                key: 'employee_edit',
                perm: (loginStatus: Record<string, any>) => {
                  if (loginStatus.hasTechAgency || !!loginStatus?.feature_flag?.is_enable_strict_employee_rule) {
                    return false;
                  }
                  return loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id;
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/employee/edit/Layout'), { fallback: <Loading /> }),
                hide: true,
                hideTabNav: true,
                showSideNav: false,
              },
              {
                label: '人户关系',
                href: '/organization/employee/advertiser-list',
                key: 'employee_advertiser_list',
                perm: (loginStatus: Record<string, any>) => loginStatus.oneOf([perms.ORGANIZATION_VIEW, 11005]) && loginStatus.company_id,
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/organization/employee/advertiser-list/Layout'), { fallback: <Loading /> }),
                hide: true,
                hideTabNav: true,
                hideSubNav: true,
                basePage: true,
                isMicroApp: false,
              },
            ],
          },
          {
            id: 'advertiserstaff',
            href: '/agency/manage-employee/advertiser',
            label: '客户员工管理',
            key: 'manage-employee-advertiser',
            disabled: (loginStatus: Record<string, any>) => loginStatus.account_type === ACCOUNT_TYPE.direct,
            data: [{
              id: 'advertiserstaff',
              href: '/agency/manage-employee/advertiser',
              label: '客户员工管理',
              key: 'manage-employee-advertiser',
              purview: 110,
              disabled: (loginStatus: Record<string, any>) => loginStatus.account_type === ACCOUNT_TYPE.direct,
              enableKey: 'is_advertiser_role_enable',
              component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/advertiserstaff'), { fallback: <Loading /> }),
              basePage: true,
            }, {
              label: '新增客户员工',
              href: '/agency/manage-employee/employee/edit',
              key: 'manage_employee_edit',
              purview: 110,
              enableKey: 'is_advertiser_role_enable',
              component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/advertiserstaff/edit'), { fallback: <Loading /> }),
              hide: true,
              hideTabNav: true,
              basePage: true,
              hideSubNav: true,
            }],
          },
        ],
      },
      {
        label: '业务管理',
        key: 'business-manage',
        iconClassName: 'icon-nav-business',
        data: [
          {
            label: '主体管理',
            key: 'manage_account',
            href: '/agency/manage-account',
            data: [{
              label: '主体管理',
              key: 'manage_account',
              href: '/agency/manage-account',
              purview: 62,
              perm: (loginStatus: Record<string, any>) => {
                if (!loginStatus.hasTechAgency && !loginStatus?.feature_flag?.is_enable_agency_invite_open) {
                  return true;
                }

                return false;
              },
              disabled: (loginStatus: Record<string, any>) => {
                // 灰度用户中的子账号员工，不可见
                if (loginStatus?.feature_flag?.is_enable_strict_employee_rule && loginStatus.operator_type === EMPLOYEE_TYPE.sub) {
                  return true;
                }

                // 单体二代没有主体管理
                return loginStatus?.agency_list?.length === 1 && loginStatus?.agency_list[0]?.parent_agency_uid;
              },
              pagePurview: {
                create_child_agency: 66,
              },
              component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/account/Layout'), { fallback: <Loading /> }),
              behavior: {
                getNewAccount: '认领新账户',
                UIDSwitch: 'UID切换按钮',
                childUIDSwitch: '子帐号UID切换按钮',
              },
              basePage: true,
            }, {
              label: '主体管理',
              key: 'manage_account',
              href: '/agency/manage-account',
              purview: 62,
              perm: (loginStatus: Record<string, any>) => {
                if (loginStatus.hasTechAgency || loginStatus?.feature_flag?.is_enable_agency_invite_open) {
                  return true;
                }

                return false;
              },
              disabled: (loginStatus: Record<string, any>) => {
                // 灰度用户中的子账号员工，不可见
                if (loginStatus?.feature_flag?.is_enable_strict_employee_rule && loginStatus.operator_type === EMPLOYEE_TYPE.sub) {
                  return true;
                }

                // 单体二代没有主体管理
                return loginStatus?.agency_list?.length === 1 && loginStatus?.agency_list[0]?.parent_agency_uid;
              },
              pagePurview: {
                create_child_agency: 66,
              },
              component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/enterprise'), { fallback: <Loading /> }),
              behavior: {
                getNewAccount: '认领新账户',
                UIDSwitch: 'UID切换按钮',
                childUIDSwitch: '子帐号UID切换按钮',
              },
              basePage: true,
            }],
          },
          {
            label: '集团管理',
            key: 'manage_bm_group',
            href: '/agency/manage-bm-group',
            purview: 94,
            disabled: (loginStatus: Record<string, any>) => !loginStatus.bm_id,
            component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/bm-group/Layout'), { fallback: <Loading /> }),
            basePage: true,
          },
          {
            key: 'label',
            label: '标签管理',
            href: '/agency/manage-label',
            purview: 2100,
            pagePurview: {
              add: 11003,
              delete: 11003,
              edit: 11003,
            },
            component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/label/Layout'), { fallback: <Loading /> }),
            basePage: true,
            singleTabNav: true,
            behavior: {
              addLabel: '新增标签',
            },
          },
          {
            label: '业务单元管理',
            key: 'agency_business_unit',
            href: '/agency/manage-bn-unit/list',
            path: '/agency/manage-bn-unit/:type',
            // 临时关闭开关
            enableKey: 'is_enable_agency_business_unit',
            purview: 8982930,
            component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/business-unit/Layout'), { fallback: <Loading /> }),
            basePage: true,
            data: [
              {
                label: '业务单元管理',
                href: '/agency/manage-bn-unit/list',
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/business-unit/create/index'), { fallback: <Loading /> }),
                enableKey: 'is_enable_agency_business_unit',
                purview: 8982930,
              },
              {
                label: '创建业务单元',
                href: '/agency/manage-bn-unit/(create|edit)',
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/business-unit/create/index'), { fallback: <Loading /> }),
                enableKey: 'is_enable_agency_business_unit',
                purview: 8982930,
                hideTabNav: true,
                hideSubNav: true,
                hide: true,
              },
              {
                label: '业务单元账户列表',
                href: '/agency/manage-bn-unit/account-list',
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/business-unit/create/components/AccountPicker/list'), { fallback: <Loading /> }),
                enableKey: 'is_enable_agency_business_unit',
                purview: 8982930,
                hideTabNav: true,
                hideSubNav: true,
                hide: true,
                basePage: true,
              },
            ],
          },
          {
            label: '备案管理',
            key: 'policy-risk',
            href: '/agency/policy/risk',
            component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/policy-risk'), { fallback: <Loading /> }),
            basePage: true,
            singleTabNav: true,
          },
          {
            label: '资质管理',
            key: 'account-qualification',
            href: '/agency/qualification/list',
            component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/account/qualification'), { fallback: <Loading /> }),
            basePage: true,
            enableKey: 'is_certification_package_management_enable',
            purview: 732546,
            data: [
              {
                label: '产品资质管理',
                href: '/agency/qualification/list',
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/account/qualification'), { fallback: <Loading /> }),
                purview: 732546,
              },
              {
                label: '创建资质',
                href: '/agency/qualification/edit',
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/account/qualification'), { fallback: <Loading /> }),
                purview: 732546,
              },
            ],
          },
          {
            id: 'contract-manage',
            breathingLight: (account: any) => {
              return account.navBreathingLight?.contractManage;
            },
            label: '合同管理',
            href: '/agency/contract-manage',
            component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/contract'), { fallback: <Loading /> }),
            purview: 11500,
            enableKey: 'is_enable_contract',
            basePage: true,
            singleTabNav: true,
          },
          {
            id: 'advertiser-illegal',
            label: '违规管理',
            href: '/agency/manage/advertiser-illegal',
            component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/advertiser-illegal'), { fallback: <Loading /> }),
            purview: 125,
            basePage: true,
            singleTabNav: true,
          },
          {
            id: 'conversion-data-governance',
            label: '转化数据回传治理',
            href: '/agency/manage/conversion-data-governance',
            component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/conversion-data-governance'), { fallback: <Loading /> }),
            basePage: true,
            singleTabNav: true,
          },
          {
            label: '财务数据填报',
            key: 'financial-data-entry',
            href: '/agency/financial-data/list',
            path: '/agency/financial-data/:type',
            disabled: (loginStatus: Record<string, any>) => !loginStatus.bm_id,
            data: [
              {
                id: 'financial-data-list',
                label: '财务数据填报',
                href: '/agency/financial-data/list',
                purview: 11520,
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/financial-data'), { fallback: <Loading /> }),
                basePage: true,
              },
              {
                id: 'financial-data-edit',
                label: '财务数据填报',
                href: '/agency/financial-data/(create|edit|view)',
                purview: 11520,
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/financial-data/edit'), { fallback: <Loading /> }),
                hide: true,
              },
            ],
          },
          {
            id: 'access-granted',
            label: '准入管理',
            href: '/agency/manage/access-granted',
            purview: 136,
            enableKey: 'enable_risk_control_reporting',
            component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/access-granted'), { fallback: <Loading /> }),
            basePage: true,
            singleTabNav: true,
          },
        ],
      },
      {
        label: '业绩管理',
        key: 'performance-manage',
        iconClassName: 'icon-nav-performance',
        data: [
          {
            label: '预估管理',
            key: 'performance-forecast-manage',
            href: '/agency/manage/performance-forecast/list',
            path: '/agency/manage/performance-forecast/:type',
            enableKey: 'is_enable_performance_forecast',
            data: [
              {
                id: 'performance-forecast-list',
                label: '预估管理',
                href: '/agency/manage/performance-forecast/list',
                purview: 141,
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/performance-forecast/list'), { fallback: <Loading /> }),
                basePage: true,
              },
              {
                id: 'performance-forecast-detail',
                label: '预估管理详情',
                href: '/agency/manage/performance-forecast/detail',
                purview: 141,
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/performance-forecast/detail'), { fallback: <Loading /> }),
                hide: true,
                hideTabNav: true,
                hideSubNav: true,
              },
            ],
          },
          {
            label: 'KPI管理',
            key: 'kpi',
            href: '/agency/manage-kpi/agent',
            path: '/agency/manage-kpi/:type',
            purview: 11002,
            enableKey: 'is_kpi_enable',
            data: [
              {
                id: 'kpi',
                label: '运营人员',
                href: '/agency/manage-kpi/agent',
                purview: 11002,
                enableKey: 'is_kpi_enable',
                pagePurview: {
                  add: 11003,
                  delete: 11003,
                  edit: 11003,
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/kpi/layout/agent-kpi'), { fallback: <Loading /> }),
                basePage: true,
                behavior: {
                  addOperatorKpi: '新增运营人员KPI',
                  addAgencyKpi: '新增服务商KPI',
                },
              },
              {
                id: 'kpi',
                label: '服务商',
                href: '/agency/manage-kpi/agency',
                purview: 11002,
                enableKey: 'is_kpi_enable',
                pagePurview: {
                  add: 11003,
                  delete: 11003,
                  edit: 11003,
                },
                component: loadable(() => import(/* webpackChunkName: 'manage' */ /* webpackMode: 'lazy' */ '@/pages/agency/manage/kpi/layout/agency-kpi'), { fallback: <Loading /> }),
                basePage: true,
                behavior: {
                  addOperatorKpi: '新增运营人员KPI',
                  addAgencyKpi: '新增服务商KPI',
                },
              },
            ],
          },
        ],
      },
    ],
  },
];
